// src/app/services/notification-preference.service.ts
import { Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ProfileService } from './profile.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationPreferenceService {
  private trInstant: (key: string | string[], interpolateParams?: Object) => any;
  constructor( private translateService: TranslateService, private fb: FormBuilder, private profileService: ProfileService) {
    this.trInstant = this.translateService.instant.bind(this.translateService);
  }

  get alarmColumns(): { key: string; field: string; translationKey: string }[] {
    return  this.alarmTypes.map((alarm) => {
      const formattedField = `notif${alarm.id
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join('')}NotificationChannelsEnabled`;

      return {
        key: alarm.id.toLowerCase(),
        field: formattedField,
        translationKey: `website.notification_config.table.columns.${alarm.id.toLowerCase()}`
      };
    });
  }


  processSingleUserNotificationPreferences(
    context: any,
    idCustomerLocation: string ,
    preferences: {
      defaultNotificationConfig?: { sms?: string[]; webpush?: string[]; email?: string[] };
      notificationConfig?: Array<{ idCustomerLocation: string; notificationDisabled: { sms?: string[]; webpush?: string[]; email?: string[] } }>;
    },
    inactive: boolean = false
  ): any {
    this.alarmTypes.forEach((alarm) => {
      const enabledChannels: string[] = [];

      this.channels.forEach((channel) => {
        const notificationDisabled = preferences?.notificationConfig
          ?.find((config) => config.idCustomerLocation === idCustomerLocation)
          ?.notificationDisabled?.[channel.id];

        const defaultConfig = preferences?.defaultNotificationConfig?.[channel.id];
        const isDisabled = notificationDisabled
          ? notificationDisabled.includes(alarm?.id)
          : defaultConfig?.includes(alarm?.id);

        if (!isDisabled) {
          enabledChannels.push(this.translateChannel(channel.translationKey));
        }
      });

      const enabledChannelsStr = enabledChannels.length > 0 ? enabledChannels.join(', ') : this.trInstant('website.notification_config.notif_disabled');
      const fieldName = `notif${alarm.
        id.split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join('')}NotificationChannelsEnabled`;

      if (inactive) {
        context[fieldName] = 'Désactivée';
      } else {
        context[fieldName] = enabledChannelsStr.trim();
      }
    });

    return context;
  }

  /**
   * Initializes the notification form dynamically
   * @param notifications List of notification objects
   * @param preferences User preferences
   * @param customerLocations Optional customer locations data
   */
  initNotificationForm(preferences: any, customerLocations: any[] = []): FormGroup {
    return this.fb.group(
      Object.fromEntries(
        this.alarmTypes.map((alarmType) => {
          const { sms, email, webpush } = this.getNotificationPreferences(
            alarmType.id,
            preferences,
            customerLocations
          );
          return [
            alarmType.id,
            this.fb.group({
              sms: new FormControl({ disabled: !this.profileService.isAdmin(), value: sms }),
              email: new FormControl(email),
              webpush: new FormControl(webpush),
            }),
          ];
        })
      )
    );
  }


  getNotificationPreferences(notificationId: string, preferences: any, customerLocations: any[] = []): { sms: boolean, email: boolean, webpush: boolean } {
    const channelStatus: { sms: boolean, email: boolean, webpush: boolean } = { sms: true, email: true, webpush: true };

    if (customerLocations.length === 1) {
      const customerLocation = customerLocations[0];
      this.channels.forEach((channel) => {
        const notificationDisabled = customerLocation.notificationPreferences?.notificationDisabled?.[channel?.id];
        const defaultConfig = preferences?.defaultNotificationConfig?.[channel?.id];

        channelStatus[channel?.id] = !(notificationDisabled ? notificationDisabled.includes(notificationId) : defaultConfig?.includes(notificationId));
      });
    } else {
      this.channels.forEach((channel) => {
        channelStatus[channel?.id] = !preferences?.defaultNotificationConfig?.[channel?.id]?.includes(notificationId);
      });
    }
    return channelStatus;
  }

  patchFormWithPreferences(form: FormGroup,  preferences: any): void {
    this.alarmTypes.forEach((alarmType) => {
      const { sms, email, webpush } = this.getNotificationPreferences(alarmType.id, preferences);
      form.get(alarmType.id)?.setValue({ sms, email, webpush });
      form.updateValueAndValidity();
    });
  }

  /**
   * Processes form data and returns the result object for saving
   * @param form The notification form
   */
  async processSaveData(form: FormGroup): Promise<{ sms: string[]; email: string[]; webpush: string[] }> {
    const formValues = form.getRawValue();
    const result = { sms: [], email: [], webpush: [] };

    Object.entries(formValues).forEach(([notificationId, types]) => {
      Object.entries(types).forEach(([type, value]) => {
        if (!value) {
          result[type as keyof typeof result].push(notificationId);
        }
      });
    });

    return result;
  }

  /**
   * Processes form data for customer locations and returns a formatted array
   * @param form The notification form
   * @param customerLocations List of customer locations
   */
  async processSaveDataForCustomerLocations(form: FormGroup, customerLocations: any[]): Promise<any[]> {
    const result = await this.processSaveData(form);
    const resultToSave = customerLocations.map((customerLocation) => ({
      idCustomerLocation: customerLocation?._id,
      notificationDisabled: result
    }));
    return resultToSave;
  }
  /**
   * Dummy translation method for notification channel names.
   */
  private translateChannel(channelLabel: string): string {
    return this.trInstant(channelLabel);
  }

  get alarmTypes(): { id: string } [] {
    return [{id: 'ALARM_FLUID'}, {id: 'ALARM_TECHNICAL'}, {id: 'ALARM_DISCONNECTION'}];
  }


  get channels(): { id: string; label: string, translationKey: string }[] {
    return [
      { id: 'email', label: 'Email', translationKey: 'website.notification_config.dialog.header.email' },
      { id: 'webpush', label: 'Web Push' , translationKey: 'website.notification_config.dialog.header.webpush' },
      { id: 'sms', label: 'SMS' , translationKey: 'website.notification_config.dialog.header.sms' },
    ];
  }

}
